/**
 * Schedule Validation
 *
 * Validates schedule definitions for completeness, correctness, and best practices.
 */

import type {
  ParsedSchedule,
  DaySchedule,
  TimeValue,
  ScheduleTypeLimits,
  ScheduleError,
  ScheduleWarning,
  ScheduleValidationResult,
  DayType,
} from './types';

/**
 * Check if time values cover the full 24-hour period
 */
function checkTimeGaps(values: TimeValue[]): { hasGap: boolean; lastTime?: string } {
  if (values.length === 0) {
    return { hasGap: true, lastTime: '00:00' };
  }

  // Sort by time
  const sorted = [...values].sort((a, b) => {
    const aMinutes = a.hour * 60 + a.minute;
    const bMinutes = b.hour * 60 + b.minute;
    return aMinutes - bMinutes;
  });

  // Check if last time is 24:00
  const last = sorted[sorted.length - 1];
  if (!last || last.hour !== 24 || last.minute !== 0) {
    return {
      hasGap: true,
      lastTime: last ? `${String(last.hour).padStart(2, '0')}:${String(last.minute).padStart(2, '0')}` : '00:00',
    };
  }

  return { hasGap: false };
}

/**
 * Check if a value is within type limits bounds
 */
function checkBounds(
  value: number,
  limits: ScheduleTypeLimits | null
): { inBounds: boolean; violation?: string } {
  if (!limits) {
    return { inBounds: true };
  }

  if (limits.lowerLimit !== undefined && value < limits.lowerLimit) {
    return {
      inBounds: false,
      violation: `Value ${String(value)} is below minimum ${String(limits.lowerLimit)}`,
    };
  }

  if (limits.upperLimit !== undefined && value > limits.upperLimit) {
    return {
      inBounds: false,
      violation: `Value ${String(value)} is above maximum ${String(limits.upperLimit)}`,
    };
  }

  return { inBounds: true };
}

/**
 * Check if all expected day types are covered
 */
function checkDayTypeCoverage(daySchedules: DaySchedule[]): DayType[] {
  const coveredDays = new Set<DayType>();

  for (const ds of daySchedules) {
    for (const dt of ds.dayTypes) {
      coveredDays.add(dt);

      // Expand aggregate types
      if (dt === 'Weekdays') {
        coveredDays.add('Monday');
        coveredDays.add('Tuesday');
        coveredDays.add('Wednesday');
        coveredDays.add('Thursday');
        coveredDays.add('Friday');
      } else if (dt === 'Weekends') {
        coveredDays.add('Saturday');
        coveredDays.add('Sunday');
      } else if (dt === 'AllDays') {
        coveredDays.add('Monday');
        coveredDays.add('Tuesday');
        coveredDays.add('Wednesday');
        coveredDays.add('Thursday');
        coveredDays.add('Friday');
        coveredDays.add('Saturday');
        coveredDays.add('Sunday');
        coveredDays.add('Holidays');
        coveredDays.add('SummerDesignDay');
        coveredDays.add('WinterDesignDay');
      }
    }
  }

  // Check for missing basic days
  const requiredDays: DayType[] = ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday', 'Sunday'];
  const missing: DayType[] = [];

  for (const day of requiredDays) {
    if (!coveredDays.has(day) && !coveredDays.has('AllDays') && !coveredDays.has('AllOtherDays')) {
      // Check if covered by aggregate
      if (
        (day === 'Monday' ||
          day === 'Tuesday' ||
          day === 'Wednesday' ||
          day === 'Thursday' ||
          day === 'Friday') &&
        !coveredDays.has('Weekdays')
      ) {
        missing.push(day);
      } else if ((day === 'Saturday' || day === 'Sunday') && !coveredDays.has('Weekends')) {
        missing.push(day);
      }
    }
  }

  return missing;
}

/**
 * Check if schedule appears to be constant (no variation)
 */
function isConstantSchedule(schedule: ParsedSchedule): boolean {
  if (schedule.scheduleType === 'Schedule:Constant') {
    return true;
  }

  // Check if all values are the same
  const allValues: number[] = [];
  for (const period of schedule.periods) {
    for (const ds of period.daySchedules) {
      for (const tv of ds.values) {
        allValues.push(tv.value);
      }
    }
  }

  if (allValues.length === 0) return true;
  const firstValue = allValues[0];
  return allValues.every((v) => v === firstValue);
}

/**
 * Check if schedule has design day values
 */
function hasDesignDays(schedule: ParsedSchedule): { summer: boolean; winter: boolean } {
  let hasSummer = false;
  let hasWinter = false;

  for (const period of schedule.periods) {
    for (const ds of period.daySchedules) {
      if (ds.dayTypes.includes('SummerDesignDay')) hasSummer = true;
      if (ds.dayTypes.includes('WinterDesignDay')) hasWinter = true;
      if (ds.dayTypes.includes('AllDays')) {
        hasSummer = true;
        hasWinter = true;
      }
    }
  }

  return { summer: hasSummer, winter: hasWinter };
}

/**
 * Check if a setpoint schedule has appropriate setback
 */
function hasSetback(schedule: ParsedSchedule): boolean {
  const name = schedule.name.toLowerCase();
  if (!name.includes('setpoint') && !name.includes('heating') && !name.includes('cooling')) {
    return true; // Not a setpoint schedule
  }

  // Check if values vary
  const allValues: number[] = [];
  for (const period of schedule.periods) {
    for (const ds of period.daySchedules) {
      for (const tv of ds.values) {
        allValues.push(tv.value);
      }
    }
  }

  if (allValues.length <= 1) return false;

  const min = Math.min(...allValues);
  const max = Math.max(...allValues);
  return max - min > 1; // At least 1 degree difference
}

/**
 * Get peak values from a schedule
 */
function getPeakValues(schedule: ParsedSchedule): { min: number; max: number; avg: number } {
  const allValues: number[] = [];

  for (const period of schedule.periods) {
    for (const ds of period.daySchedules) {
      for (const tv of ds.values) {
        allValues.push(tv.value);
      }
    }
  }

  if (allValues.length === 0) {
    return { min: 0, max: 0, avg: 0 };
  }

  const min = Math.min(...allValues);
  const max = Math.max(...allValues);
  const sum = allValues.reduce((a, b) => a + b, 0);
  const avg = sum / allValues.length;

  return { min, max, avg };
}

/**
 * Validate a single schedule
 */
export function validateSchedule(
  schedule: ParsedSchedule,
  typeLimits: ScheduleTypeLimits | null = null
): ScheduleValidationResult {
  const errors: ScheduleError[] = [];
  const warnings: ScheduleWarning[] = [];

  // Skip validation for unsupported schedule types with no periods
  if (schedule.periods.length === 0) {
    return { isValid: true, errors: [], warnings: [] };
  }

  // Check each period
  for (const period of schedule.periods) {
    // Check each day schedule
    for (const daySchedule of period.daySchedules) {
      // Check for time gaps
      const gapCheck = checkTimeGaps(daySchedule.values);
      if (gapCheck.hasGap) {
        const gapError: ScheduleError = {
          type: 'gap',
          message: `Schedule does not cover until 24:00 for ${daySchedule.dayTypes.join(', ')}`,
          location: {
            periodId: period.id,
            dayType: daySchedule.dayTypes.join(', '),
          },
          suggestion: 'Add a final entry with Until: 24:00',
        };
        if (gapCheck.lastTime !== undefined) {
          gapError.location!.time = gapCheck.lastTime;
        }
        errors.push(gapError);
      }

      // Check bounds for each value
      if (typeLimits) {
        for (const tv of daySchedule.values) {
          const boundsCheck = checkBounds(tv.value, typeLimits);
          if (!boundsCheck.inBounds) {
            errors.push({
              type: 'bounds',
              message: boundsCheck.violation || 'Value out of bounds',
              location: {
                periodId: period.id,
                dayType: daySchedule.dayTypes.join(', '),
                time: `${String(tv.hour).padStart(2, '0')}:${String(tv.minute).padStart(2, '0')}`,
              },
              suggestion: `Adjust value to be within ${String(typeLimits.lowerLimit ?? '-Inf')} - ${String(typeLimits.upperLimit ?? 'Inf')}`,
            });
          }
        }
      }
    }

    // Check day type coverage
    const missingDays = checkDayTypeCoverage(period.daySchedules);
    if (missingDays.length > 0) {
      warnings.push({
        type: 'unusual-value',
        message: `Period may not cover all day types: ${missingDays.join(', ')}`,
        suggestion: 'Consider adding schedules for missing day types or using AllOtherDays',
      });
    }
  }

  // Check for design days
  const designDays = hasDesignDays(schedule);
  if (!designDays.summer && !designDays.winter) {
    warnings.push({
      type: 'no-design-day',
      message: 'Schedule has no design day values',
      suggestion: 'Consider adding SummerDesignDay and WinterDesignDay entries for sizing calculations',
    });
  } else if (!designDays.summer) {
    warnings.push({
      type: 'no-design-day',
      message: 'Schedule has no SummerDesignDay values',
      suggestion: 'Consider adding SummerDesignDay entry for cooling sizing',
    });
  } else if (!designDays.winter) {
    warnings.push({
      type: 'no-design-day',
      message: 'Schedule has no WinterDesignDay values',
      suggestion: 'Consider adding WinterDesignDay entry for heating sizing',
    });
  }

  // Check for constant setpoint schedules
  if (!hasSetback(schedule)) {
    const name = schedule.name.toLowerCase();
    if (name.includes('setpoint') || name.includes('heating') || name.includes('cooling')) {
      warnings.push({
        type: 'no-setback',
        message: 'Setpoint schedule has no setback periods',
        suggestion: 'Consider adding night/weekend setback for energy savings',
      });
    }
  }

  // Check for "always on" schedules that shouldn't be
  const peaks = getPeakValues(schedule);
  if (isConstantSchedule(schedule) && peaks.max === 1) {
    const name = schedule.name.toLowerCase();
    if (name.includes('occupancy') || name.includes('lighting') || name.includes('equipment')) {
      warnings.push({
        type: 'always-on',
        message: 'Schedule appears to be always on (constant value of 1)',
        suggestion: 'Consider adding realistic operating hours for accurate simulation',
      });
    }
  }

  return {
    isValid: errors.length === 0,
    errors,
    warnings,
  };
}

/**
 * Validate all schedules in a collection
 */
export function validateSchedules(
  schedules: ParsedSchedule[],
  getTypeLimits: (name: string) => ScheduleTypeLimits | undefined
): Map<string, ScheduleValidationResult> {
  const results = new Map<string, ScheduleValidationResult>();

  for (const schedule of schedules) {
    const limits = schedule.typeLimits ? getTypeLimits(schedule.typeLimits) ?? null : null;
    results.set(schedule.id, validateSchedule(schedule, limits));
  }

  return results;
}

/**
 * Get validation summary for display
 */
export function getValidationSummary(result: ScheduleValidationResult): {
  hasErrors: boolean;
  hasWarnings: boolean;
  errorCount: number;
  warningCount: number;
  summary: string;
} {
  const hasErrors = result.errors.length > 0;
  const hasWarnings = result.warnings.length > 0;

  let summary = '';
  if (hasErrors && hasWarnings) {
    summary = `${String(result.errors.length)} error${result.errors.length > 1 ? 's' : ''}, ${String(result.warnings.length)} warning${result.warnings.length > 1 ? 's' : ''}`;
  } else if (hasErrors) {
    summary = `${String(result.errors.length)} error${result.errors.length > 1 ? 's' : ''}`;
  } else if (hasWarnings) {
    summary = `${String(result.warnings.length)} warning${result.warnings.length > 1 ? 's' : ''}`;
  } else {
    summary = 'Valid';
  }

  return {
    hasErrors,
    hasWarnings,
    errorCount: result.errors.length,
    warningCount: result.warnings.length,
    summary,
  };
}
